<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { DsButton, DsText } from '@coloplast/design-system';
import { type InvestorQuickLinkBlockProps, type StockData } from './InvestorQuickLinkBlock.types';

const props = defineProps<InvestorQuickLinkBlockProps>();

const stockData = ref<StockData | null>(null);

/* For reviewers and future reference - the labels are intentionally hardcoded in this block, the block is always in English and the values are fixed */
const LABELS = {
	MARKET: "CPH:",
	STOCK_DISPLAY_NAME: "Coloplast B",
	OPEN: "Open",
	LAST_CLOSE: "Last close",
}

const FEED_URL = 'https://tools.eurolandir.com/tools/pricefeed/?companycode=dk-colo&format=json'

const fetchStockData = async () => {
	try {
		const response = await fetch(FEED_URL);
		const data = await response.json();
		const stockKey = Object.keys(data)[0];
		stockData.value = data[stockKey] as StockData;
	} catch (error) {
		console.error('Failed to fetch stock data', error);
	}
};

const getNumberValue = (value: string | number | undefined): number => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

const roundToTwo = (value: string) =>
  getNumberValue(value).toFixed(2);

const formatStockDate = (dateString: string): string => {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  return new Intl.DateTimeFormat("en-GB", options).format(date);
};


const postiveOrNegativeChange = computed(() => {
	const changeNumber = getNumberValue(stockData.value?.Change);
	if (changeNumber > 0) return "positive";
	else if (changeNumber < 0) return "negative";
	return "neutral";
});

const changeClass = computed(() => {
	return `c-shareprice-card__change--${postiveOrNegativeChange.value}`;
});

const ctaIconStart = computed(() => props?.linkIconPosition == "Start" ? props.linkIcon : undefined)
const ctaIconEnd = computed(() => props?.linkIconPosition == "End" ? props.linkIcon : undefined)

onMounted(fetchStockData);
</script>

<template>
	<div  class="c-shareprice-card">
		<transition name="fade">
			<div v-if="stockData" class="c-shareprice-card__top">
				<DsText text-type="body-xs" text-style="regular" html-element="div" class="c-shareprice-card__top-line">
					<span class="c-shareprice-card__label"
						><strong class="c-shareprice-card__market">{{ LABELS.MARKET }}</strong> {{ LABELS.STOCK_DISPLAY_NAME }} ({{
							stockData.Symbol
						}})</span
					>
					<strong class="c-shareprice-card__currency">{{
						stockData.Currency
					}}</strong>
				</DsText>
				<div class="c-shareprice-card__mid">
					<DsText text-type="body-xs" text-style="regular" html-element="div" class="c-shareprice-card__change-line">
						<span class="c-shareprice-card__change" :class="changeClass">{{ roundToTwo(stockData.Change) }}</span>
						<span>{{ roundToTwo(stockData.ChangePercent) }}%</span>
					</DsText>
					<DsText text-type="heading-7xl" text-style="regular" html-element="p" class="c-shareprice-card__price">{{ roundToTwo(stockData.Last) }}</DsText>
					<DsText text-type="body-xs" html-element="div" class="c-shareprice-card__day-line">
						<strong class="c-shareprice-card__open-close">{{ roundToTwo(stockData.Open) }}</strong><span class="c-shareprice-card__open-close-label">{{ LABELS.OPEN }}</span>
						<span class="c-shareprice-card__middot"></span>
						<strong class="c-shareprice-card__open-close">{{ roundToTwo(stockData.LastClose) }}</strong><span class="c-shareprice-card__open-close-label">{{ LABELS.LAST_CLOSE }}</span>
						<span class="c-shareprice-card__middot"></span>
						{{ formatStockDate(stockData.Date) }}
					</DsText>
				</div>
			</div>
		</transition>
		<DsButton v-if="linkText && linkUrl"
			class="c-shareprice-card__cta"
			:href="linkUrl"
			size="sm"
			variant="primary"
			:icon-start="ctaIconStart"
			:icon-end="ctaIconEnd"
			:target="linkTarget"
			>
			{{ linkText }}
		</DsButton>
	</div>
</template>

<style lang="scss" scoped>
@import 'shared/styles/settings/settings';
@import 'shared/styles/tools/tools';

.c-shareprice-card {
	--card-padding: #{ds-space(8)};
	--change-color: transparent;

	background: ds-color('bg', 'default');
	border-radius: ds-border-radius('md');
	display: flex;
	block-size: 100%;
	padding: var(--card-padding);
	flex-direction: column;
	gap: ds-space(11);
	justify-content: end;

	.fade-enter-active,
	.fade-leave-active {
		transition: all ds-duration("medium-2") ds-easing("enter");
	}

	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
	}

	&__top {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		justify-content: space-between;
	}

	&__top-line {
		align-items: center;
		display: flex;
		color: ds-color("fg", "default");
		justify-content: space-between;
	}

	&__label {
		border-radius: ds-border-radius(sm);
		background: ds-color("bg", "neutral");
		color: ds-color("fg", "muted");
		display: inline-block;
		padding: ds-space(2) ds-space(3) ds-space(1) ds-space(3);
	}

	&__currency {
		font-weight: ds-font-weight("medium");
	}

	&__change-line {
		align-items: center;
		column-gap: ds-gap(5);
		display: flex;
		margin-block-end: ds-gap(5);
	}

	&__change {
		background: var(--change-color);
		border-radius: ds-border-radius(sm);
		display: inline-block;
		font-weight: ds-font-weight("medium");
		padding: 4px 7px 3px 7px;

		&--positive {
			--change-color: rgba(136, 190, 117, 0.5);
		}

		&--negative {
			--change-color: rgba(238, 111, 137, 0.5);
		}
	}

	&__market {
		color: ds-color("fg", "default");
	}

	&__day-line {
		display: flex;
		flex-wrap: wrap;
	}

	&__open-close {
		margin-inline-end: 3px;
	}

	&__price {
		margin: 0 0 ds-gap(4);
	}

	&__middot {
		align-items: center;
		display: inline-flex;
		margin-inline: ds-gap(2);

		&::before {
			background: ds-color("fg", "muted");
			block-size: 2px;
			border-radius: ds-border-radius(round);
			content: "";
			display: block;
			position: absolute;
			inline-size: 2px;
		}
	}

	&__cta {
		align-self: flex-end;
	}
}
</style>
